import React from 'react';
import { Formik, Form } from 'formik';
import Input from '@/components/Input';
import Label from '@/components/Label';
import Button from '@/components/Button';

interface DataField {
  _id: string;
  label: string;
  description?: string;
  required: boolean;
  type: 'text' | 'email' | 'phone' | 'number';
}

interface DataCollectionFormProps {
  dataFields: DataField[];
  onSubmit: (data: Record<string, string>) => void;
  onCancel: () => void;
  primaryColor?: string;
  isSubmitting?: boolean;
  hideFooter?: boolean;
  onFooterRender?: (footerContent: React.ReactNode) => void;
}

const DataCollectionForm: React.FC<DataCollectionFormProps> = ({
  dataFields,
  onSubmit,
  onCancel,
  primaryColor,
  isSubmitting = false,
  hideFooter = false
}) => {
  // Create initial values
  const initialValues = React.useMemo(() => {
    const values: Record<string, string> = {};
    dataFields?.forEach(field => {
      values[field._id] = '';
    });
    return values;
  }, [dataFields]);

  // Validation function
  const validate = (values: Record<string, string>) => {
    const errors: Record<string, string> = {};

    dataFields?.forEach(field => {
      if (field.required && !values[field._id]?.trim()) {
        errors[field._id] = `${field.label} is required`;
      }
      if (field.type === 'email' && values[field._id] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values[field._id])) {
        errors[field._id] = 'Please enter a valid email address';
      }
      if (field.type === 'phone' && values[field._id] && !/^\+?[\d\s\-\(\)]+$/.test(values[field._id].replace(/\s/g, ''))) {
        errors[field._id] = 'Please enter a valid phone number';
      }
    });

    return errors;
  };

  // Handle form submission
  const handleSubmit = (values: Record<string, string>, { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }) => {
    setSubmitting(true);
    onSubmit(values);
    setSubmitting(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      validate={validate}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ errors, touched, values, handleChange, handleBlur, isValid }) => (
        <Form>
          <div className='p-4 space-y-3'>
            {dataFields?.map((field) => (
              <div key={field._id} className="space-y-2 ">
                <Label
                  htmlFor={field._id}
                  className="text-sm font-medium text-gray-700 dark:text-white"
                >
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </Label>
                {field.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-300">
                    {field.description}
                  </p>
                )}
                <Input
                  id={field._id}
                  name={field._id}
                  type={field.type === 'email' ? 'email' :
                    field.type === 'phone' ? 'tel' :
                      field.type === 'number' ? 'number' : 'text'}
                  value={values[field._id] || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={`Enter your ${field.label.toLowerCase()}`}
                  disabled={isSubmitting}
                  error={touched[field._id] && !!errors[field._id]}
                  helperText={touched[field._id] ? errors[field._id] : ''}
                  className="w-full"
                />
              </div>
            ))}
          </div>
          {!hideFooter && (
            <div className="flex gap-3 p-4 border-t border-gray-200 dark:border-gray-600">
              <Button
                type="button"
                variant="secondary"
                onClick={onCancel}
                disabled={isSubmitting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={!isValid || isSubmitting}
                loading={isSubmitting}
                className="flex-1"
                style={{ backgroundColor: primaryColor }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default DataCollectionForm;
